import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Alert,
  CircularProgress,
  Autocomplete,
  Chip,
} from '@mui/material'
import { useForm, Controller } from 'react-hook-form'
import { useAppSelector, useAppDispatch } from '@hooks/redux'
import { fetchAlertRule, createAlertRule, updateAlertRule, deleteAlertRule } from '@store/slices/alertRulesSlice'
import { fetchApiariesAndGroups } from '@store/slices/locationsSlice'
import { alertsApi, MeasurementType } from '@api/alerts'
import { AlertRule } from '@types'
import SaveToolbar from '@components/common/SaveToolbar'

interface AlertRuleFormData {
  name: string
  description: string
  is_active: boolean
  alert_via_email: boolean
  alert_via_push: boolean
  alert_via_sms: boolean
  calculation_minutes: number
  // '' means "any sensor type" (measurement_type left unset); otherwise the
  // MeasurementType id as a string, since MUI Select values are strings.
  measurement_type: string
  calculation: string
  comparison_operator: string
  threshold_value: number
  cooldown_minutes: number
  max_alerts_per_day: number
  webhook_url?: string
  severity: 'low' | 'medium' | 'high' | 'critical'
  apply_to_all_hives: boolean
  include_hive_ids: number[]
}

const calculationOptions = [
  { value: 5, label: '5 minutes' },
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 60, label: '1 hour' },
  { value: 180, label: '3 hours' },
  { value: 360, label: '6 hours' },
  { value: 720, label: '12 hours' },
  { value: 1440, label: '24 hours' },
]

const comparisonOptions = [
  { value: '>', label: 'Greater than (>)' },
  { value: '<', label: 'Less than (<)' },
  { value: '>=', label: 'Greater than or equal (≥)' },
  { value: '<=', label: 'Less than or equal (≤)' },
  { value: '==', label: 'Equal to (=)' },
  { value: '!=', label: 'Not equal to (≠)' },
]

// Matches AlertService._resolve_rule_value: anything outside this set falls
// back to "most recent value" server-side.
const calculationTypeOptions = [
  { value: '', label: 'Most recent value' },
  { value: 'avg', label: 'Average' },
  { value: 'min', label: 'Minimum' },
  { value: 'max', label: 'Maximum' },
]

const severityOptions = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
]

/**
 * AlertRuleEdit Page Component
 *
 * Create or edit alert rules for monitoring hive conditions.
 * Features:
 * - Configure alert conditions and thresholds
 * - Set calculation periods
 * - Enable/disable email, push, and SMS notifications
 * - Preview alert rule summary
 *
 * @component
 */
const AlertRuleEdit: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const dispatch = useAppDispatch()

  const isCreateMode = !id
  const { selectedAlertRule, loadingState, error } = useAppSelector((state) => state.alertRules)
  const { hives } = useAppSelector((state) => state.locations)

  const [showLoadingIcon, setShowLoadingIcon] = useState(false)
  const [measurementTypes, setMeasurementTypes] = useState<MeasurementType[]>([])

  const { control, handleSubmit, watch, setValue, formState: { errors } } = useForm<AlertRuleFormData>({
    defaultValues: {
      name: '',
      description: '',
      is_active: true,
      alert_via_email: true,
      alert_via_push: true,
      alert_via_sms: false,
      calculation_minutes: 60,
      measurement_type: '',
      calculation: '',
      comparison_operator: '>',
      threshold_value: 0,
      cooldown_minutes: 60,
      max_alerts_per_day: 10,
      webhook_url: '',
      severity: 'medium',
      apply_to_all_hives: true,
      include_hive_ids: [],
    },
  })

  const watchedValues = watch()

  useEffect(() => {
    dispatch(fetchApiariesAndGroups())
    alertsApi.getMeasurementTypes().then((res) => {
      setMeasurementTypes(res.data.data)
    }).catch((err) => {
      console.error('Failed to load measurement types:', err)
    })
  }, [dispatch])

  useEffect(() => {
    if (id && !isCreateMode) {
      dispatch(fetchAlertRule(Number(id)))
    }
  }, [dispatch, id, isCreateMode])

  useEffect(() => {
    if (selectedAlertRule && !isCreateMode) {
      setValue('name', selectedAlertRule.name)
      setValue('description', selectedAlertRule.description || '')
      setValue('is_active', selectedAlertRule.is_active)
      setValue('alert_via_email', selectedAlertRule.alert_via_email)
      setValue('alert_via_push', selectedAlertRule.alert_via_push)
      setValue('alert_via_sms', selectedAlertRule.alert_via_sms)
      setValue('calculation_minutes', selectedAlertRule.calculation_minutes || 60)
      setValue('measurement_type', selectedAlertRule.measurement_type ? String(selectedAlertRule.measurement_type) : '')
      setValue('calculation', selectedAlertRule.calculation || '')
      setValue('comparison_operator', selectedAlertRule.comparison_operator || '>')
      setValue('threshold_value', selectedAlertRule.threshold_value ?? 0)
      setValue('cooldown_minutes', selectedAlertRule.cooldown_minutes ?? 60)
      setValue('max_alerts_per_day', selectedAlertRule.max_alerts_per_day ?? 10)
      setValue('webhook_url', selectedAlertRule.webhook_url || '')
      setValue('severity', selectedAlertRule.severity || 'medium')
      setValue('apply_to_all_hives', selectedAlertRule.apply_to_all_hives ?? true)
      setValue('include_hive_ids', selectedAlertRule.include_hive_ids || [])
    }
  }, [selectedAlertRule, isCreateMode, setValue])

  const generateAlertSummary = () => {
    const { name, measurement_type, calculation, comparison_operator, threshold_value, calculation_minutes } = watchedValues

    if (!name) {
      return 'Configure your alert rule to see a preview'
    }

    const measurement = measurementTypes.find((m) => String(m.id) === measurement_type)?.name || 'any sensor'
    const calcType = calculationTypeOptions.find((c) => c.value === calculation)?.label || 'most recent value'
    const time = calculationOptions.find((c) => c.value === calculation_minutes)?.label || `${calculation_minutes}m`

    return `Alert "${name}" will trigger when the ${calcType.toLowerCase()} of ${measurement} over ${time} is ${comparison_operator} ${threshold_value}`
  }

  const onSubmit = async (data: AlertRuleFormData) => {
    setShowLoadingIcon(true)
    try {
      const { measurement_type, threshold_value, cooldown_minutes, max_alerts_per_day, include_hive_ids, ...rest } = data
      const payload: Partial<AlertRule> = {
        ...rest,
        rule_type: 'threshold',
        threshold_value: Number(threshold_value),
        cooldown_minutes: Number(cooldown_minutes),
        max_alerts_per_day: Number(max_alerts_per_day),
        measurement_type: measurement_type ? Number(measurement_type) : null,
        include_hive_ids: rest.apply_to_all_hives ? [] : include_hive_ids,
      }

      if (isCreateMode) {
        await dispatch(createAlertRule(payload)).unwrap()
      } else {
        await dispatch(updateAlertRule({ id: Number(id), data: payload })).unwrap()
      }
      navigate('/alertrules')
    } catch (err) {
      console.error('Failed to save alert rule:', err)
    } finally {
      setShowLoadingIcon(false)
    }
  }

  const handleDelete = async () => {
    if (window.confirm(`Are you sure you want to delete alert rule "${watchedValues.name}"?`)) {
      setShowLoadingIcon(true)
      await dispatch(deleteAlertRule(Number(id)))
      navigate('/alertrules')
    }
  }

  if (loadingState === 'pending' && !isCreateMode) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={50} />
        </Box>
      </Container>
    )
  }

  return (
    <Container maxWidth="lg" sx={{ py: 2, px: { xs: 2, sm: 3 } }}>
      <SaveToolbar
        onSave={handleSubmit(onSubmit)}
        onDelete={!isCreateMode ? handleDelete : undefined}
        saveDisabled={showLoadingIcon}
        loading={showLoadingIcon}
        showDelete={!isCreateMode}
      />

      <Typography variant="h4" component="h1" gutterBottom sx={{ mt: 2 }}>
        {isCreateMode ? 'New Alert Rule' : 'Edit Alert Rule'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Alert Summary */}
      <Card sx={{ mb: 3, bgcolor: watchedValues.is_active ? 'primary.main' : 'grey.400' }}>
        <CardContent>
          <Typography variant="overline" display="block" gutterBottom>
            Alert Rule Summary
          </Typography>
          {!watchedValues.is_active && (
            <Typography variant="body2" color="error" gutterBottom>
              This alert rule is not active
            </Typography>
          )}
          <Typography variant="body1" sx={{ color: '#000' }}>
            {generateAlertSummary()}
          </Typography>
        </CardContent>
      </Card>

      {/* Settings Form */}
      <Card>
        <CardContent>
          <Typography variant="overline" display="block" gutterBottom>
            Alert Rule Settings
          </Typography>

          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
              {/* Name and Status */}
              <Grid item xs={12} md={7}>
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: 'Name is required', maxLength: 255 }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Name"
                      error={!!errors.name}
                      helperText={errors.name?.message}
                      inputProps={{ maxLength: 255 }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={6} md={2}>
                <Controller
                  name="is_active"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Switch {...field} checked={field.value} color="primary" />}
                      label="Active"
                    />
                  )}
                />
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Description"
                      multiline
                      rows={2}
                      inputProps={{ maxLength: 250 }}
                    />
                  )}
                />
              </Grid>

              {/* Notification channels */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Controller
                    name="alert_via_email"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch {...field} checked={field.value} color="primary" />}
                        label="Email"
                      />
                    )}
                  />
                  <Controller
                    name="alert_via_push"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch {...field} checked={field.value} color="primary" />}
                        label="Push"
                      />
                    )}
                  />
                  <Controller
                    name="alert_via_sms"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch {...field} checked={field.value} color="primary" />}
                        label="SMS"
                      />
                    )}
                  />
                </Box>
              </Grid>

              {/* Calculation Time */}
              <Grid item xs={12} md={6}>
                <Controller
                  name="calculation_minutes"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Calculation Period</InputLabel>
                      <Select {...field} label="Calculation Period">
                        {calculationOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Sensor type */}
              <Grid item xs={12} md={6}>
                <Controller
                  name="measurement_type"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Sensor</InputLabel>
                      <Select {...field} label="Sensor">
                        <MenuItem value="">Any sensor</MenuItem>
                        {measurementTypes.map((mt) => (
                          <MenuItem key={mt.id} value={String(mt.id)}>
                            {mt.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Calculation Type */}
              <Grid item xs={12} md={4}>
                <Controller
                  name="calculation"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Calculation Type</InputLabel>
                      <Select {...field} label="Calculation Type">
                        {calculationTypeOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Comparison */}
              <Grid item xs={12} md={4}>
                <Controller
                  name="comparison_operator"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Comparison</InputLabel>
                      <Select {...field} label="Comparison">
                        {comparisonOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Threshold Value */}
              <Grid item xs={12} md={4}>
                <Controller
                  name="threshold_value"
                  control={control}
                  rules={{ required: 'Threshold value is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Threshold Value"
                      type="number"
                      error={!!errors.threshold_value}
                      helperText={errors.threshold_value?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name="severity"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Severity</InputLabel>
                      <Select {...field} label="Severity">
                        {severityOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={8}>
                <Controller
                  name="apply_to_all_hives"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Switch {...field} checked={field.value} color="primary" />}
                      label="Apply to all hives"
                    />
                  )}
                />
              </Grid>

              {!watchedValues.apply_to_all_hives && (
                <Grid item xs={12}>
                  <Controller
                    name="include_hive_ids"
                    control={control}
                    render={({ field }) => (
                      <Autocomplete
                        multiple
                        options={hives}
                        getOptionLabel={(option) => option.name}
                        value={hives.filter((hive) => field.value.includes(hive.id))}
                        onChange={(_, selected) => field.onChange(selected.map((hive) => hive.id))}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip label={option.name} {...getTagProps({ index })} key={option.id} />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Hives"
                            helperText="Choose which hives this rule should watch"
                          />
                        )}
                      />
                    )}
                  />
                </Grid>
              )}

              {/* Cooldown */}
              <Grid item xs={12} md={6}>
                <Controller
                  name="cooldown_minutes"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Cooldown (minutes)"
                      type="number"
                      helperText="Minimum time between alerts from this rule"
                      inputProps={{ min: 0 }}
                    />
                  )}
                />
              </Grid>

              {/* Max alerts per day */}
              <Grid item xs={12} md={6}>
                <Controller
                  name="max_alerts_per_day"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Max alerts per day"
                      type="number"
                      inputProps={{ min: 1 }}
                    />
                  )}
                />
              </Grid>

              {/* Webhook URL */}
              <Grid item xs={12}>
                <Controller
                  name="webhook_url"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Webhook URL (optional)"
                      placeholder="https://example.com/webhook"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Container>
  )
}

export default AlertRuleEdit
